import type { ProductEntity } from '@/entities/assortment'
import type {
	FieldSchemaModel,
	ProductsQueryItemModel,
} from '@/shared/generated'

import { useMemo } from 'react'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { getEntityFieldValue } from '@/entities/assortment'
import { fields } from '@/entities/column'
import { usePublicSettingQuery } from '@/entities/core'
import { useFieldSchemasQuery } from '@/entities/field-schema'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

const getCellComponent = (field: FieldSchemaModel) => {
	if (field.field_type && fields[field.field_type]) {
		return fields[field.field_type].cellComponent
	}

	return fields.text.cellComponent
}
type SettingsBody = {
	custom_metric_field_id: string
	new_margin_field_id: string
}
export const SkuSidebarConfigurableField = ({
	content,
	configKey,
}: {
	content: ProductEntity | ProductsQueryItemModel
	/**
	 * The key of the configurable field in the settings body
	 */
	configKey: 'custom_metric_field_id' | 'new_margin_field_id'
}) => {
	const { t } = useTranslation()
	const fieldsQuery = useFieldSchemasQuery()
	const settingsQuery = usePublicSettingQuery(
		'product-detail-settings:product-detail-settings'
	)
	const field = useMemo(
		() =>
			settingsQuery.data?.body
				? fieldsQuery.data?.find(
						(field) =>
							field.id === (settingsQuery.data?.body as SettingsBody)[configKey]
					)
				: null,
		[fieldsQuery.data, settingsQuery.data, configKey]
	)

	if (
		!field ||
		fieldsQuery.isLoading ||
		settingsQuery.isLoading ||
		fieldsQuery.isError ||
		settingsQuery.isError ||
		!settingsQuery.data?.body ||
		!configKey
	)
		return null

	return (
		<DescriptionGroup>
			<Tooltip>
				<TooltipTrigger asChild>
					<DescriptionTerm className='text-wrap'>
						{t(field.name, {
							ns: 'fields',
						})}
					</DescriptionTerm>
				</TooltipTrigger>
				<TooltipContent>
					{t(field.name, {
						ns: 'fields',
					})}
				</TooltipContent>
			</Tooltip>
			<DescriptionDetail className='flex items-center gap-1 font-bold'>
				<ErrorBoundary
					fallback={
						<div className='flex items-center gap-1 text-sm text-destructive'>
							<AlertTriangleIcon className='size-4' />
							{t('app.error', { defaultValue: 'Error' })}
						</div>
					}
				>
					<MetricValue content={content} field={field} />
				</ErrorBoundary>
			</DescriptionDetail>
		</DescriptionGroup>
	)
}

const MetricValue = ({
	content,
	field,
}: {
	content: ProductEntity | ProductsQueryItemModel
	field: FieldSchemaModel
}) => {
	const CellComponent = getCellComponent(field) as any
	const value = getEntityFieldValue(content, field.id)

	return (
		<CellComponent className='justify-start! text-left!' value={value as any} />
	)
}
